// FRIDAY-specific API calls (operational layer on top of CRM)
import { api } from '@/lib/api-client'
import type { CampaignEmail, CampaignEmailDetail as EmailDetail, CampaignEmailPage, EmailAction, EmailAudienceSourcePage, EmailContactResult, EmailContactHistory, EmailVersion, EmailVersionPage } from '@/types/campaign-email'
import type {
  Invoice, XeroLedgerInvoice, Contract, Campaign, CampaignDetail, CampaignUpdate, RateCard, Proposal, Alert,
  Opportunity, ContentIdea, ContentAssessment,
} from '@/types/api'

// Invoices
export const invoicesApi = {
  list: (opportunity_id?: string) => {
    const qs = opportunity_id ? `?opportunity_id=${opportunity_id}` : ''
    return api.get<{ invoices: Invoice[] }>(`/invoices${qs}`)
  },
  // The synced Xero ledger (receivables + bills). Owner-only on the server.
  listXero: (type?: 'ACCREC' | 'ACCPAY') => {
    const qs = type ? `?type=${type}` : ''
    return api.get<{ invoices: XeroLedgerInvoice[] }>(`/invoices/xero${qs}`)
  },
  // The invoice PDF, proxied from Xero (authed) → Blob.
  xeroPdf: (id: string) => api.getBlob(`/invoices/xero/${id}/pdf`),
  create: (body: { opportunity_id: string; type: string; amount_cents: number; due_date?: number }) =>
    api.post<Invoice>('/invoices', body),
  update: (id: string, body: { status?: string; due_date?: number; paid_at?: number }) =>
    api.patch<Invoice>(`/invoices/${id}`, body),
  pushToXero: (id: string) => api.post<Invoice>(`/invoices/${id}/push-to-xero`, {}),
}

// Contracts
export const contractsApi = {
  list: () => api.get<{ contracts: Contract[] }>('/contracts'),
  create: (body: Partial<Contract>) => api.post<Contract>('/contracts', body),
  update: (id: string, body: Partial<Contract>) => api.patch<Contract>(`/contracts/${id}`, body),
  renew: (id: string, body?: { end_date?: number }) =>
    api.post<{ new_contract: Contract; old_contract: Contract }>(`/contracts/${id}/renew`, body ?? {}),
}

// Campaigns
export const campaignsApi = {
  list: () => api.get<{ campaigns: Campaign[] }>('/campaigns'),
  get: (id: string) => api.get<CampaignDetail>(`/campaigns/${id}`),
  create: (body: Partial<Campaign>) => api.post<Campaign>('/campaigns', body),
  update: (id: string, body: CampaignUpdate) => api.patch<Campaign>(`/campaigns/${id}`, body),
  createShortlink: (id: string, body: { destination_url: string; slug: string }) =>
    api.post<{ short_url: string }>(`/campaigns/${id}/shortlinks`, body),
}

export const campaignEmailsApi = {
  list: (campaignId?: string, offset = 0) => {
    const query = new URLSearchParams({ offset: String(offset) })
    if (campaignId) query.set('campaign_id', campaignId)
    return api.get<CampaignEmailPage>(`/email-campaigns?${query}`)
  },
  get: (id: string) => api.get<EmailDetail>(`/email-campaigns/${encodeURIComponent(id)}`),
  versions: (id: string, offset = 0) => api.get<EmailVersionPage>(`/email-campaigns/${encodeURIComponent(id)}/versions?offset=${offset}`),
  version: (id: string, versionId: string) => api.get<{ version: EmailVersion }>(`/email-campaigns/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`),
  sources: (id: string, offset = 0) => api.get<EmailAudienceSourcePage>(`/email-campaigns/${encodeURIComponent(id)}/audience-sources?offset=${offset}`),
  removeSource: (id: string, sourceId: string) => api.delete(`/email-campaigns/${encodeURIComponent(id)}/audience/${encodeURIComponent(sourceId)}`),
  addContacts: (id: string, contactIds: string[]) => api.post<EmailContactResult>(`/email-campaigns/${encodeURIComponent(id)}/audience/contacts`, { contact_ids: contactIds }),
  contactHistory: (id: string) => api.get<{ history: EmailContactHistory[] }>(`/email-campaigns/parties/${encodeURIComponent(id)}/history`),
  act: (id: string, action: Exclude<EmailAction, 'remove-audience'>, body: Record<string, unknown> = {}) =>
    api.post<{ email?: CampaignEmail }>(`/email-campaigns/${encodeURIComponent(id)}/${action}`, body),
}

// Rate Cards
export const rateCardsApi = {
  list: () => api.get<{ rate_cards: RateCard[] }>('/rate-cards'),
  get: (id: string) => api.get<RateCard>(`/rate-cards/${id}`),
  create: (body: { scope: string; lines?: Array<{ role: string; unit: string; sell_rate_cents: number }> }) =>
    api.post<RateCard>('/rate-cards', body),
}

// Proposals
export const proposalsApi = {
  list: (opportunity_id?: string) => {
    const qs = opportunity_id ? `?opportunity_id=${opportunity_id}` : ''
    return api.get<{ proposals: Proposal[] }>(`/proposals${qs}`)
  },
  create: (body: {
    opportunity_id: string
    rate_card_id?: string
    notes?: string
    lines?: Array<{ description: string; quantity: number; unit_price_cents: number; service_id?: string }>
  }) => api.post<Proposal>('/proposals', body),
  update: (id: string, body: { status?: 'draft' | 'sent' | 'accepted' | 'declined'; notes?: string }) =>
    api.patch<Proposal>(`/proposals/${id}`, body),
  // Proposal documents synced from the Sales SharePoint library (owner-only).
  listDocuments: () => api.get<{ documents: ProposalDocument[] }>('/proposals/documents'),
  // Reading analytics for tracked proposal pages (owner/admin-only).
  analytics: (scope: { opportunity_id?: string; party_id?: string; refs?: string }) => {
    const qs = new URLSearchParams(
      Object.entries(scope).filter(([, v]) => Boolean(v)) as [string, string][],
    ).toString()
    return api.get<ProposalAnalytics>(`/proposals/analytics?${qs}`)
  },
  syncSharePoint: () => api.post<{ scanned: number; upserted: number }>('/proposals/sync', {}),
}

export interface ProposalAnalytics {
  refs: string[]
  generatedAt: string
  configured: boolean
  error?: string
  summary: {
    readers: number
    opens: number
    gateShown: number
    gatePassed: number
    wrongCodes: number
    esignClicks: number
    lastSeen: string | null
  } | null
  sections: Array<{ section: string; readers: number }>
  depth: Array<{ depth: number; readers: number }>
  options: Array<{ option: string; clicks: number }>
  demoTabs: Array<{ pane: string; clicks: number }>
}

export interface ProposalDocument {
  id: string
  title: string
  sharepoint_url: string | null
  updated_at: number
  party_id: string | null
  party_name: string | null
  opportunity_id: string | null
  opportunity_title: string | null
}

// Alerts
export const alertsApi = {
  list: () => api.get<{ alerts: Alert[] }>('/alerts'),
}

// Engagements (used by proposal / invoice create forms)
export const opportunitiesApi = {
  // The engagement + pursuit concepts merged into a single opportunity pipeline.
  // Kept the `engagements` field name so existing callers (proposal/invoice link
  // pickers) keep working against the unified list.
  list: async () => {
    const { opportunities } = await api.get<{ opportunities: Opportunity[] }>('/pipeline')
    return { engagements: opportunities }
  },
}

export type Service = {
  id: string
  code: string
  name: string
  category: string
  unit: string
  default_role: string | null
  notes: string | null
  default_price_cents: number | null
  price_unit: string | null
}

// Services catalog (used by the proposal builder)
export const servicesApi = {
  list: (category?: string) => {
    const qs = category ? `?category=${category}` : ''
    return api.get<{ services: Service[] }>(`/services${qs}`)
  },
}

export const contentIdeasApi = {
  list: (params?: { pipeline?: string; status?: string }) => {
    const qs = new URLSearchParams()
    if (params?.pipeline) qs.set('pipeline', params.pipeline)
    if (params?.status) qs.set('status', params.status)
    const q = qs.toString()
    return api.get<{ ideas: ContentIdea[] }>(`/content/ideas${q ? `?${q}` : ''}`)
  },
  create: (body: {
    pipeline: string; title: string; category?: string;
    icp?: string; angle?: string; seo_phrase?: string; notes?: string;
  }) => api.post<ContentIdea>('/content/ideas', body),
  update: (id: string, body: Partial<Pick<ContentIdea, 'status' | 'title' | 'category' | 'icp' | 'angle' | 'seo_phrase' | 'notes'>>) =>
    api.patch<ContentIdea>(`/content/ideas/${id}`, body),
  assess: (id: string, body: ContentAssessment) =>
    api.post<ContentIdea>(`/content/ideas/${id}/assess`, body),
  delete: (id: string) =>
    api.delete<{ deleted: boolean }>(`/content/ideas/${id}`),
  aiAssess: (id: string) =>
    api.post<ContentIdea>(`/content/ideas/${id}/ai-assess`, {}),
}
